import { IonAlert, IonIcon, IonImg, IonText, useIonRouter } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import {
  enterOutline,
  checkboxOutline,
  exitOutline,
  printOutline,
  personCircle,
  settingsOutline,
} from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import ChipsGroup from '@components/ChipsGroup';
import DetailsList, { DetailsListItem } from '@components/DetailsList';
import { ButtonType } from '@components/Layout';
import RoutePath from '@custom-types/RoutePath';
import { useCheckActionModal } from '@hooks/useCheckActionModal';
import useLoading from '@hooks/useLoading';
import { usePrintBadgeModal } from '@hooks/usePrintBadgeModal';
import { usePrinter } from '@hooks/usePrinter';
import { CheckAction, CheckEntity } from '@pages/Check/Enums';
import { getLatestCheck } from '@pages/Check/service';
import { formatDateTime } from '@services/date-service';
import { generateRoute } from '@services/navigation-service';
import { getCheckOutEnabled, getEventInfo, getPeopleGroupsVisibility } from '@state/event';
import { getSessionInfo } from '@state/session';
import { getLocale } from '@state/system';

import { get as getFn, getKiosk as getKioskFn, Person } from './service';
import { PeopleProfileMode } from './types';

const Footer = styled.div`
  h3 {
    font-weight: 500;
    margin: 4px 0;
  }
  h3,
  p {
    font-size: 1.25rem;
  }
`;

const Image = styled(IonImg)`
  margin: 16px 0;
  width: 180px;
  height: 180px;
`;

const Icon = styled(IonIcon)`
  width: 180px;
  height: 180px;
`;

const Subtitle = styled(IonText)`
  font-size: 20px;
  margin: 4px 0;
  text-align: center;
`;

const selector = createSelector(
  getEventInfo,
  getSessionInfo,
  getCheckOutEnabled,
  getLocale,
  getPeopleGroupsVisibility,
  ({ id: eventId, name: eventName }, session, isCheckOutEnabled, locale, showPeopleGroups) => ({
    eventId,
    eventName,
    sessionName: session?.name,
    entityType: session?.id ? CheckEntity.Session : CheckEntity.Event,
    entityId: session?.id || eventId,
    isCheckOutEnabled,
    locale,
    showPeopleGroups,
  })
);

const usePerson = (eventId: string, isKiosk = false) => {
  const { personIdOrEmail } = useParams<{ personIdOrEmail: string }>();
  const [person, setPerson] = useState<Person>();
  const [error, setError] = useState('');
  const { presentLoading, dismissLoading } = useLoading();

  useEffect(() => {
    const effect = async () => {
      await presentLoading();
      let response: Person;

      try {
        if (isKiosk) {
          [response] = (await getKioskFn(eventId, personIdOrEmail)).data;
        } else {
          response = await getFn(eventId, personIdOrEmail);
        }
        setPerson(response);
      } catch {
        setError(t`We were unable to process your request at this time. Please try again`);
      } finally {
        await dismissLoading();
      }
    };

    // skipcq: JS-0328
    effect();
  }, [eventId, dismissLoading, presentLoading, personIdOrEmail, isKiosk]);

  return {
    person,
    error,
    presentLoading,
    dismissLoading,
    setError,
  };
};

const Details = () => {
  const {
    eventId,
    eventName,
    sessionName,
    entityType,
    entityId,
    isCheckOutEnabled,
    locale,
    showPeopleGroups,
  } = useSelector(selector);
  const { mode } = useParams<{ mode?: PeopleProfileMode }>();
  const { push: pushRoute } = useIonRouter();
  const { person, error, setError } = usePerson(eventId);
  const { enabled: isPrintingEnabled } = usePrinter();

  const checkActionProps = {
    title: entityType === CheckEntity.Session ? sessionName! : eventName,
    entityType,
    entityId,
  };

  const { show: showCheckInModal } = useCheckActionModal({
    action: CheckAction.CheckIn,
    goBack: true,
    ...checkActionProps,
  });

  const { show: showCheckOutModal } = useCheckActionModal({
    action: CheckAction.CheckOut,
    goBack: true,
    ...checkActionProps,
  });

  const [showPrintBadgeModal] = usePrintBadgeModal({});

  const onPrinterSettingsClick = useCallback(() => {
    pushRoute(generateRoute(RoutePath.PrinterSettings, { eventId }));
  }, [eventId, pushRoute]);

  if (!eventId) {
    return null;
  }

  const checks = person?.checkins?.filter(check => check.entityType === entityType);
  const latestCheckIn = getLatestCheck(checks ?? [], CheckAction.CheckIn);
  const latestCheckOut = getLatestCheck(checks ?? [], CheckAction.CheckOut);
  const latestCheckInString = latestCheckIn
    ? formatDateTime({ utcDate: latestCheckIn.checkinAt, locale })
    : 'N/A';
  const latestCheckOutString = latestCheckOut
    ? formatDateTime({ utcDate: latestCheckOut.checkinAt, locale })
    : 'N/A';
  const entityName = entityType === CheckEntity.Event ? t`the Event` : sessionName;

  const listItems: DetailsListItem[] = [];
  if (!mode || mode === PeopleProfileMode.Check) {
    listItems.push({
      // skipcq: JS-0052
      // skipcq: JS-0338
      onClick: () => showCheckInModal(person?.checkinCode!),
      icon: enterOutline,
      title: t`Check in to ${entityName}`,
      subtitle: t`Last Check In: ${latestCheckInString}`,
    });
  }
  if (
    isPrintingEnabled &&
    entityType === CheckEntity.Event &&
    (!mode || mode === PeopleProfileMode.Check)
  ) {
    listItems.push({
      // skipcq: JS-0052
      onClick: () =>
        // skipcq: JS-0338
        showPrintBadgeModal({ checkInCode: person?.checkinCode!, shouldCheckIn: true }),
      icon: checkboxOutline,
      title: t`Check in & Print Badge`,
      subtitle: t`Last Check In: ${latestCheckInString}`,
    });
  }
  if (isCheckOutEnabled && (!mode || mode === PeopleProfileMode.Check)) {
    listItems.push({
      // skipcq: JS-0052
      // skipcq: JS-0338
      onClick: () => showCheckOutModal(person?.checkinCode!),
      icon: exitOutline,
      title: t`Check Out of ${entityName}`,
      subtitle: t`Last Check Out: ${latestCheckOutString}`,
    });
  }
  if (isPrintingEnabled && entityType === CheckEntity.Event) {
    listItems.push({
      // skipcq: JS-0052, JS-0338
      onClick: () => showPrintBadgeModal({ checkInCode: person?.checkinCode! }),
      icon: printOutline,
      title: t`Print Badge only`,
    });
  }
  if (isPrintingEnabled && entityType === CheckEntity.Event) {
    listItems.push({
      // skipcq: JS-0052, JS-0338
      onClick: onPrinterSettingsClick,
      icon: settingsOutline,
      title: t`Manage your device settings`,
      subtitle: t`Set up your printer for onsite badge printing.`,
    });
  }

  return (
    <DetailsList
      pageTitle={eventName}
      header={
        person && (
          <>
            {person.profileImage ? (
              <Image src={person.profileImage.thumbnailUrl} />
            ) : (
              <Icon icon={personCircle} />
            )}
            <h3>
              {person.firstName} {person.lastName}
            </h3>
            {person.pronouns && <Subtitle color="medium">({person.pronouns})</Subtitle>}
            <Subtitle>{person.email}</Subtitle>
            {person.companyName && <Subtitle>{person.companyName}</Subtitle>}
            {person.title && <Subtitle>{person.title}</Subtitle>}
            {showPeopleGroups && person.groups && person.groups.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <ChipsGroup labels={person.groups.map(({ name }) => name)} />
              </div>
            )}
          </>
        )
      }
      callToAction={t`What would you like to do?`}
      buttonType={ButtonType.Back}
      listItems={listItems}
      footer={
        isPrintingEnabled &&
        entityType === CheckEntity.Event && (
          <Footer>
            <IonAlert
              isOpen={!!error}
              // skipcq: JS-0417
              onDidDismiss={() => setError('')}
              header={t`Service Error`}
              message={error}
              buttons={[t`OK`]}
            />
          </Footer>
        )
      }
    />
  );
};

export default Details;
